import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Switch } from './ui/switch';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Alert, AlertDescription } from './ui/alert';
import { UserPlus, Truck, Shield, Loader2, Save, AlertTriangle, CheckCircle } from 'lucide-react';
import axios from 'axios';
import { toast } from '../hooks/use-toast';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const RegistrationConfigPanel = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [config, setConfig] = useState({
    client_registration_enabled: true,
    driver_registration_enabled: true,
    require_email_verification: true,
    require_phone_verification: false,
    require_admin_approval_client: false,
    require_admin_approval_driver: true,
    registration_closed_message: "Les inscriptions sont temporairement fermées. Contactez l'administrateur."
  });

  useEffect(() => {
    fetchConfig();
  }, []);

  const fetchConfig = async () => {
    try {
      const response = await axios.get(`${API}/system-config/section/registration`, {});
      
      if (response.data) {
        setConfig(prev => ({ ...prev, ...response.data }));
      }
    } catch (error) {
      console.error('Error fetching registration config:', error);
      // Utiliser les valeurs par défaut
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(
        `${API}/system-config/`,
        {
          section: 'registration',
          data: config
        },
        {}
      );
      
      toast({
        title: "✅ Configuration enregistrée",
        description: "Les paramètres d'inscription ont été mis à jour"
      });
    } catch (error) {
      console.error('Error saving registration config:', error);
      toast({
        title: "Erreur",
        description: error.response?.data?.detail || "Impossible de sauvegarder la configuration",
        variant: "destructive"
      });
    } finally {
      setSaving(false);
    }
  };

  const handleToggle = (field) => {
    setConfig(prev => ({ ...prev, [field]: !prev[field] }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Warning si inscriptions désactivées */}
      {(!config.client_registration_enabled || !config.driver_registration_enabled) && (
        <Alert className="bg-amber-50 border-amber-200">
          <AlertTriangle className="w-4 h-4 text-amber-600" />
          <AlertDescription className="text-amber-800">
            <strong>Mode restreint actif :</strong> Certaines inscriptions sont désactivées. 
            Seuls les comptes créés par l'administrateur peuvent accéder à la plateforme.
          </AlertDescription>
        </Alert>
      )}

      {/* Inscription Clients */}
      <Card>
        <CardHeader className="pb-4">
          <CardTitle className="flex items-center gap-2 text-lg">
            <UserPlus className="w-5 h-5 text-blue-600" />
            Inscription Clients
          </CardTitle>
          <CardDescription>
            Contrôlez si les clients peuvent s&apos;inscrire eux-mêmes sur la plateforme
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
            <div>
              <Label className="text-base font-medium">Autoriser l&apos;inscription des clients</Label>
              <p className="text-sm text-gray-500 mt-1">
                Si désactivé, seul l&apos;admin peut créer des comptes clients
              </p>
            </div>
            <Switch
              checked={config.client_registration_enabled}
              onCheckedChange={() => handleToggle('client_registration_enabled')}
            />
          </div>

          {config.client_registration_enabled && (
            <>
              <div className="flex items-center justify-between p-3 border rounded-lg">
                <div>
                  <Label className="text-sm font-medium">Vérification email obligatoire</Label>
                  <p className="text-xs text-gray-500">Le client doit vérifier son email avant de se connecter</p>
                </div>
                <Switch
                  checked={config.require_email_verification}
                  onCheckedChange={() => handleToggle('require_email_verification')}
                />
              </div>

              <div className="flex items-center justify-between p-3 border rounded-lg">
                <div>
                  <Label className="text-sm font-medium">Approbation admin requise</Label>
                  <p className="text-xs text-gray-500">L&apos;admin doit valider chaque nouveau client</p>
                </div>
                <Switch
                  checked={config.require_admin_approval_client}
                  onCheckedChange={() => handleToggle('require_admin_approval_client')}
                />
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* Inscription Chauffeurs */}
      <Card>
        <CardHeader className="pb-4">
          <CardTitle className="flex items-center gap-2 text-lg">
            <Truck className="w-5 h-5 text-green-600" />
            Inscription Chauffeurs
          </CardTitle>
          <CardDescription>
            Contrôlez si les chauffeurs peuvent s&apos;inscrire eux-mêmes sur la plateforme
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
            <div>
              <Label className="text-base font-medium">Autoriser l&apos;inscription des chauffeurs</Label>
              <p className="text-sm text-gray-500 mt-1">
                Si désactivé, seul l&apos;admin peut créer des comptes chauffeurs
              </p>
            </div>
            <Switch
              checked={config.driver_registration_enabled}
              onCheckedChange={() => handleToggle('driver_registration_enabled')}
            />
          </div>

          {config.driver_registration_enabled && (
            <div className="flex items-center justify-between p-3 border rounded-lg">
              <div>
                <Label className="text-sm font-medium">Approbation admin requise</Label>
                <p className="text-xs text-gray-500">L&apos;admin doit valider chaque nouveau chauffeur</p>
              </div>
              <Switch
                checked={config.require_admin_approval_driver}
                onCheckedChange={() => handleToggle('require_admin_approval_driver')}
              />
            </div>
          )}
        </CardContent>
      </Card>

      {/* Message personnalisé */}
      <Card>
        <CardHeader className="pb-4">
          <CardTitle className="flex items-center gap-2 text-lg">
            <Shield className="w-5 h-5 text-purple-600" />
            Message d&apos;inscription fermée
          </CardTitle>
          <CardDescription>
            Message affiché aux visiteurs quand les inscriptions sont désactivées
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Textarea
            value={config.registration_closed_message}
            onChange={(e) => setConfig(prev => ({ ...prev, registration_closed_message: e.target.value }))}
            placeholder="Les inscriptions sont temporairement fermées..."
            rows={3}
            className="resize-none"
          />
        </CardContent>
      </Card>

      {/* Bouton Sauvegarder */}
      <div className="flex justify-end">
        <Button
          onClick={handleSave}
          disabled={saving}
          className="bg-blue-600 hover:bg-blue-700"
        >
          {saving ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin mr-2" />
              Enregistrement...
            </>
          ) : (
            <>
              <Save className="w-4 h-4 mr-2" />
              Enregistrer les paramètres
            </>
          )}
        </Button>
      </div>

      {/* Statut actuel */}
      <Card className="bg-gray-50">
        <CardContent className="pt-4">
          <div className="flex items-center gap-4 text-sm">
            <div className="flex items-center gap-2">
              {config.client_registration_enabled ? (
                <CheckCircle className="w-4 h-4 text-green-500" />
              ) : (
                <AlertTriangle className="w-4 h-4 text-red-500" />
              )}
              <span>Clients: {config.client_registration_enabled ? 'Ouvert' : 'Fermé'}</span>
            </div>
            <div className="flex items-center gap-2">
              {config.driver_registration_enabled ? (
                <CheckCircle className="w-4 h-4 text-green-500" />
              ) : (
                <AlertTriangle className="w-4 h-4 text-red-500" />
              )}
              <span>Chauffeurs: {config.driver_registration_enabled ? 'Ouvert' : 'Fermé'}</span>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default RegistrationConfigPanel;
